import React, { useEffect, useRef } from "react";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import {
  PolicyPageWrapper,
  PolicyHeroSection,
  PolicyContainer,
} from "../Policy/styles";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Support = () => {
  const heroRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        hero.querySelector(".heroLabel"),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", delay: 0.1 }
      );

      gsap.fromTo(
        hero.querySelector("h1"),
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.2 }
      );

      gsap.fromTo(
        hero.querySelector(".heroDescription"),
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.4 }
      );

      gsap.fromTo(
        hero.querySelectorAll(".badge"),
        { opacity: 0, y: 20, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "back.out(1.7)",
          delay: 0.5,
        }
      );
    }, hero);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const ctx = gsap.context(() => {
      const cards = content.querySelectorAll(".support-card");
      gsap.fromTo(
        cards,
        { opacity: 0, y: 50, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: content,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      const faqs = content.querySelectorAll(".faq-item");
      gsap.fromTo(
        faqs,
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: faqs[0],
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, content);

    return () => ctx.revert();
  }, []);

  const supportOptions = [
    {
      title: "Phone Support",
      description: "Speak directly with our technical experts for immediate assistance.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
        </svg>
      ),
      contact: "+965 2224 1234",
      hours: "Sun-Thu: 9AM - 6PM",
    },
    {
      title: "Email Support",
      description: "Send us your queries and get detailed responses within 24 hours.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      ),
      contact: "support@saharacomputers.com",
      hours: "24/7 Response",
    },
    {
      title: "On-Site Support",
      description: "Our technicians can visit your location for complex issues.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      ),
      contact: "Schedule a Visit",
      hours: "By Appointment",
    },
    {
      title: "Remote Support",
      description: "Quick remote assistance for software and configuration issues.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      contact: "Connect Now",
      hours: "Sun-Thu: 9AM - 6PM",
    },
  ];

  const faqs = [
    {
      question: "What is the warranty period for HP products?",
      answer: "Most HP products come with a standard 1-year warranty. Extended warranty options are available for purchase. Please check the specific product page or contact us for details.",
    },
    {
      question: "Do you offer Annual Maintenance Contracts (AMC)?",
      answer: "Yes, we offer comprehensive AMC services for servers, desktops, notebooks, printers, and networks. We have different contract types based on response time requirements.",
    },
    {
      question: "What is your 'No Solution, No Charge' policy?",
      answer: "If we are unable to resolve your computer problems, there will be no charge for our services. However, if upgrades or replacements are required, standard charges apply.",
    },
    {
      question: "How quickly can you respond to service requests?",
      answer: "Response times vary based on your service agreement. Standard response is within 24-48 hours. Priority contracts offer 4-8 hour response times.",
    },
    {
      question: "Do you provide services outside Kuwait City?",
      answer: "Yes, we provide services throughout Kuwait. Additional travel charges may apply for locations outside Kuwait City.",
    },
  ];

  return (
    <PolicyPageWrapper>
      <Header />

      <PolicyHeroSection ref={heroRef}>
        <div className="heroBg">
          <div className="gradientOrb orb1" />
          <div className="gradientOrb orb2" />
          <div className="gridPattern" />
        </div>

        <div className="floatingShape shape1" />
        <div className="floatingShape shape2" />
        <div className="floatingShape shape3" />

        <div className="heroContent">
          <div className="heroLabel">
            <span className="line" />
            <span>Help Center</span>
            <span className="line" />
          </div>

          <h1>
            Technical <span className="highlight">Support</span>
          </h1>

          <p className="heroDescription">
            Our expert team is ready to help you with any IT issues. Get fast,
            reliable support for all your technology needs.
          </p>

          <div className="heroBadges">
            <div className="badge">
              <div className="badgeIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <div className="badgeText">
                <div className="badgeNumber">24/7</div>
                <div className="badgeLabel">Email Support</div>
              </div>
            </div>
            <div className="badge">
              <div className="badgeIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <div className="badgeText">
                <div className="badgeNumber">30 Day</div>
                <div className="badgeLabel">Service Warranty</div>
              </div>
            </div>
          </div>
        </div>
      </PolicyHeroSection>

      <PolicyContainer>
        <ContentWrapper ref={contentRef}>
          <SectionTitle>How Can We Help You?</SectionTitle>

          <SupportGrid>
            {supportOptions.map((option, index) => (
              <SupportCard key={index} className="support-card">
                <div className="icon-wrapper">{option.icon}</div>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
                <div className="contact-info">
                  <span className="contact">{option.contact}</span>
                  <span className="hours">{option.hours}</span>
                </div>
              </SupportCard>
            ))}
          </SupportGrid>

          <SectionTitle>Frequently Asked Questions</SectionTitle>

          <FAQList>
            {faqs.map((faq, index) => (
              <FAQItem key={index} className="faq-item">
                <h4>
                  <span className="number">{String(index + 1).padStart(2, "0")}</span>
                  {faq.question}
                </h4>
                <p>{faq.answer}</p>
              </FAQItem>
            ))}
          </FAQList>

          <CTASection>
            <h3>Still Need Help?</h3>
            <p>
              Our support team is always ready to assist you. Reach out to us
              through any of the channels above or visit our office.
            </p>
            <a href="/#contactUs" className="cta-button">
              Contact Us
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </CTASection>
        </ContentWrapper>
      </PolicyContainer>

      <Footer />
    </PolicyPageWrapper>
  );
};

export default Support;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1000px;
`;

const SectionTitle = styled.h2`
  font-family: Poppins;
  font-size: 36px;
  font-weight: 700;
  color: var(--text-primary);
  text-align: center;
  margin: 0 0 48px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SupportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SupportCard = styled.div`
  background: var(--card-bg);
  border-radius: 20px;
  padding: 32px;
  border: 2px solid var(--card-border);
  transition: all 0.4s ease;
  will-change: transform, opacity;

  &:hover {
    transform: translateY(-8px);
    border-color: var(--accent);
    box-shadow: 0 25px 60px var(--shadow-medium);

    .icon-wrapper {
      background: var(--accent);

      svg {
        color: #fff;
      }
    }
  }

  .icon-wrapper {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(217, 58, 53, 0.1) 0%, rgba(43, 59, 124, 0.1) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    transition: all 0.3s ease;

    svg {
      width: 28px;
      height: 28px;
      color: var(--accent);
      transition: color 0.3s ease;
    }
  }

  h3 {
    font-family: Poppins;
    font-size: 22px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 12px;
  }

  p {
    font-family: Poppins;
    font-size: 15px;
    line-height: 1.7;
    color: var(--text-secondary);
    margin: 0 0 20px;
  }

  .contact-info {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .contact {
      font-family: Poppins;
      font-size: 16px;
      font-weight: 600;
      color: var(--accent);
    }

    .hours {
      font-family: Poppins;
      font-size: 13px;
      color: var(--text-secondary);
    }
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 80px;
`;

const FAQItem = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 28px 32px;
  border: 2px solid var(--card-border);
  transition: all 0.3s ease;
  will-change: transform, opacity;

  &:hover {
    border-color: var(--primary);
  }

  h4 {
    font-family: Poppins;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 12px;
    display: flex;
    align-items: flex-start;
    gap: 16px;

    .number {
      color: var(--accent);
      font-size: 14px;
      font-weight: 700;
      min-width: 28px;
    }
  }

  p {
    font-family: Poppins;
    font-size: 15px;
    line-height: 1.8;
    color: var(--text-secondary);
    margin: 0;
    padding-left: 44px;
  }

  @media (max-width: 768px) {
    padding: 20px 24px;

    h4 {
      font-size: 16px;
    }

    p {
      padding-left: 0;
    }
  }
`;

const CTASection = styled.div`
  text-align: center;
  padding: 60px 40px;
  background: linear-gradient(135deg, var(--primary) 0%, #1e2a5e 100%);
  border-radius: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  h3 {
    font-family: Poppins;
    font-size: 32px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 16px;
    position: relative;
    z-index: 1;
  }

  p {
    font-family: Poppins;
    font-size: 16px;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.9);
    max-width: 500px;
    margin: 0 auto 28px;
    position: relative;
    z-index: 1;
  }

  .cta-button {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 16px 36px;
    background: var(--accent);
    color: #fff;
    font-family: Poppins;
    font-size: 16px;
    font-weight: 600;
    border-radius: 14px;
    text-decoration: none;
    transition: all 0.3s ease;
    position: relative;
    z-index: 1;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(217, 58, 53, 0.4);
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }

  @media (max-width: 768px) {
    padding: 40px 24px;

    h3 {
      font-size: 24px;
    }
  }
`;
